/// <amd-module name="Favorites/Common/FavoritesService" />

import { IReadonlyObservableArray, ObservableArray } from "VSS/Core/Observable";
import { IVssObservableService, IVssPageContext, Services, VssObservableService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssSecurityService } from "VSS/Platform/Security";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { Favorite, FavoriteProvider } from "./Contracts/Favorite";
import { IFavoriteRestClient } from "./RestClient/Favorite";

/**
 * Service for managing favorite artifacts.
 */
export interface IFavoritesService extends IVssObservableService<Favorite | Error> {
    /**
     * Describes if the current user has general read and write permissions on the Favorites Service.
     * Encapsulates implementation details of how to perform that check (Suitable mock-point for permission agnostic UT's)
     */
    canUseFavorites: () => boolean;

    /**
     * Whether the service supports to query favorites with extended details (probably slower).
     */
    canFetchExtendedDetails: () => boolean;

    /**
     * Get favorites of the given artifact type
     */
    getFavorites: (
        artifactType: string,
        artifactScopeType?: string,
        artifactScopeId?: string,
        includeExtendedDetails?: boolean
    ) => Promise<IFavoritesCollection>;

    /**
     * Get team favorites of the given artifact type
     */
    getTeamFavorites: (
        teamId: string,
        artifactType: string,
        artifactScopeType?: string,
        artifactScopeId?: string,
        includeExtendedDetails?: boolean
    ) => Promise<IFavoritesCollection>;

    /**
     * Get all favorites for the given artifact scope type and identifier.
     */
    getFavoritesForScope: (artifactScopeType: string, artifactScopeId?: string, includeExtendedDetails?: boolean) => Promise<IFavoritesCollection>;

    /**
     * Add (favorite) the given item
     */
    addFavorite: (favorite: Favorite) => Promise<Favorite>;

    /**
     * Add (favorite) the given item for a team
     */
    addTeamFavorite: (teamId: string, favorite: Favorite) => Promise<Favorite>;

    /**
     * Remove (un-favorite) the given favorite item
     */
    removeFavorite: (favorite: Favorite) => Promise<void>;

    /**
     * Remove (un-favorite) the given favorite item for a team
     */
    removeTeamFavorite: (teamId: string, favorite: Favorite) => Promise<void>;

    /**
     * Get the favorite provider for the  artifact type provided.
     */
    getProvider: (artifactType: string) => Promise<FavoriteProvider | null>;

    /**
     * Get all the favorite providers.
     */
    getProviders: () => Promise<{ [artifactType: string]: FavoriteProvider }>;
}

/**
 * Collection of favorite items
 */
export interface IFavoritesCollection extends IReadonlyObservableArray<Favorite> {
    /**
     * Dispose this collection, removing all its subscribers
     */
    dispose: () => void;
}

class FavoritesCollection extends ObservableArray<Favorite> implements IFavoritesCollection {
    constructor(
        private pageContext: IVssPageContext,
        favorites: Favorite[],
        private artifactType: string | undefined,
        private artifactScopeType?: string,
        private artifactScopeId?: string
    ) {
        super(favorites);

        const service = this.pageContext.getService<IFavoritesService>("IFavoritesService");
        service.subscribe(this.onFavoriteAdded, "favoriteAdded");
        service.subscribe(this.onFavoriteRemoved, "favoriteRemoved");
    }

    public dispose() {
        const service = this.pageContext.getService<IFavoritesService>("IFavoritesService");
        service.unsubscribe(this.onFavoriteAdded, "favoriteAdded");
        service.unsubscribe(this.onFavoriteRemoved, "favoriteRemoved");
    }

    private onFavoriteAdded = (favorite: Favorite): void => {
        if (this.isAssociatedFavorite(favorite)) {
            this.push(favorite);
        }
    };

    private onFavoriteRemoved = (favorite: Favorite): void => {
        if (this.isAssociatedFavorite(favorite)) {
            this.removeAll(f => f.id === favorite.id);
        }
    };

    private isAssociatedFavorite(favorite: Favorite): boolean {
        return (
            (!this.artifactType || favorite.artifactType === this.artifactType) &&
            (!this.artifactScopeId || (favorite.artifactScope.type === this.artifactScopeType && favorite.artifactScope.id === this.artifactScopeId))
        );
    }
}

/**
 * Implements a modern, cached view of favorites, covering read and write operations.
 */
class FavoritesService extends VssObservableService<Favorite | Error> implements IFavoritesService {
    private providersPromise: Promise<{ [artifactType: string]: FavoriteProvider }>;
    private favoriteCollections: { [key: string]: Promise<FavoritesCollection> } = {};

    /**
     * Describes if the current user has general read and write permissions on the Favorites Service.
     * Encapsulates implementation details of how to perform that check (Suitable mock-point for permission agnostic UT's)
     */
    public canUseFavorites(): boolean {
        const feature = this.pageContext.getService<IVssSecurityService>("IVssSecurityService");

        const favoritesNamespace = "fa557b48-b5bf-458a-bb2b-1b680426fe8b";
        const securityToken = "/Favorites";
        const readAndWrite = 3;

        const securityService = this.pageContext.getService<IVssSecurityService>("IVssSecurityService");
        return securityService.hasPermission(favoritesNamespace, securityToken, readAndWrite);
    }

    public canFetchExtendedDetails(): boolean {
        return true;
    }

    public getFavorites(
        artifactType: string,
        artifactScopeType?: string,
        artifactScopeId?: string,
        includeExtendedDetails?: boolean
    ): Promise<IFavoritesCollection> {
        if (!this.canUseFavorites()) {
            const emptyFavoriteCollection = new FavoritesCollection(this.pageContext, [], artifactType, artifactScopeType, artifactScopeId);
            return Promise.resolve(emptyFavoriteCollection);
        }

        const key = `${artifactType}:${artifactScopeType}:${artifactScopeId}:${includeExtendedDetails}`;
        const cachedCollection = this.favoriteCollections[key];
        if (cachedCollection) {
            return cachedCollection;
        }

        const promise = this.getProvider(artifactType).then(provider => {
            const favoritesClient = this.pageContext.getRestClient<IFavoriteRestClient>("IFavoriteRestClient", {
                serviceInstanceType: (provider as FavoriteProvider).serviceIdentifier
            });
            return favoritesClient.getFavorites(artifactType, artifactScopeType, artifactScopeId, includeExtendedDetails).then(favorites => {
                return new FavoritesCollection(this.pageContext, favorites, artifactType, artifactScopeType, artifactScopeId);
            });
        });

        this.favoriteCollections[key] = promise;

        return promise;
    }

    public getTeamFavorites(
        teamId: string,
        artifactType: string,
        artifactScopeType?: string,
        artifactScopeId?: string,
        includeExtendedDetails?: boolean
    ): Promise<IFavoritesCollection> {
        if (!this.canUseFavorites()) {
            const emptyFavoriteCollection = new FavoritesCollection(this.pageContext, [], artifactType, artifactScopeType, artifactScopeId);
            return Promise.resolve(emptyFavoriteCollection);
        }

        const key = `${artifactType}:${artifactScopeType}:${artifactScopeId}:${includeExtendedDetails}:${teamId}`;
        const cachedCollection = this.favoriteCollections[key];
        if (cachedCollection) {
            return cachedCollection;
        }

        const promise = this.getProvider(artifactType).then(provider => {
            const favoritesClient = this.pageContext.getRestClient<IFavoriteRestClient>("IFavoriteRestClient", {
                serviceInstanceType: (provider as FavoriteProvider).serviceIdentifier,
                rootPath: (provider as FavoriteProvider).serviceUri
            });
            return favoritesClient
                .getFavoritesOfOwner("Team", teamId, artifactType, artifactScopeType, artifactScopeId, includeExtendedDetails)
                .then(favorites => {
                    return new FavoritesCollection(this.pageContext, favorites, artifactType, artifactScopeType, artifactScopeId);
                });
        });

        this.favoriteCollections[key] = promise;

        return promise;
    }

    public getFavoritesForScope(
        artifactScopeType: string,
        artifactScopeId?: string,
        includeExtendedDetails?: boolean
    ): Promise<IFavoritesCollection> {
        if (!this.canUseFavorites()) {
            const emptyFavoriteCollection = new FavoritesCollection(this.pageContext, [], undefined, artifactScopeType, artifactScopeId);
            return Promise.resolve(emptyFavoriteCollection);
        }

        const key = `${artifactScopeType}:${artifactScopeId}:${includeExtendedDetails}`;
        const cachedCollection = this.favoriteCollections[key];
        if (cachedCollection) {
            return cachedCollection;
        }

        const promise = this.getProviders().then((providerMap: { [artifactType: string]: FavoriteProvider }) => {
            const favoritesByServiceInstancePromises: { [serviceInstance: string]: Promise<Favorite[]> } = {};

            for (const artifactType in providerMap) {
                const provider = providerMap[artifactType];
                if (!favoritesByServiceInstancePromises[provider.serviceUri]) {
                    const favoritesClient = this.pageContext.getRestClient<IFavoriteRestClient>("IFavoriteRestClient", {
                        serviceInstanceType: provider.serviceIdentifier
                    });
                    const favoritesPromise = favoritesClient.getFavorites(undefined, artifactScopeType, artifactScopeId, includeExtendedDetails);
                    favoritesByServiceInstancePromises[provider.serviceUri] = favoritesPromise;
                }
            }

            const dataPromises: Promise<Favorite[]>[] = [];
            const promises = Object.keys(favoritesByServiceInstancePromises).map(k => favoritesByServiceInstancePromises[k]);
            promises.forEach(promise => {
                dataPromises.push(
                    promise.then(
                        value => {
                            return value;
                        },
                        reason => {
                            // we return an empty array if a federated source does not load properly. This provides a graceful experience to not blow up the UI that depends on getting the
                            // collection of favorites across providers.
                            const telemetryService = this.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
                            telemetryService.publishEvent("Favorites", "FavoritesService", reason);
                            return [];
                        }
                    )
                );
            });

            return Promise.all(dataPromises).then((value: Favorite[][]) => {
                return new FavoritesCollection(this.pageContext, ([] as Favorite[]).concat(...value), undefined, artifactScopeType, artifactScopeId);
            });
        });

        this.favoriteCollections[key] = promise;

        return promise;
    }

    /**
     * Add (favorite) the given item
     */
    public addFavorite(favorite: Favorite): Promise<Favorite> {
        if (!this.canUseFavorites()) {
            this.logNoEditPermissionError();
            return Promise.resolve(favorite);
        }

        return this.getProvider(favorite.artifactType).then(
            provider => {
                const favoritesClient = this.pageContext.getRestClient<IFavoriteRestClient>("IFavoriteRestClient", {
                    serviceInstanceType: (provider as FavoriteProvider).serviceIdentifier
                });
                return favoritesClient.createFavorite(favorite).then(
                    favorite => {
                        this._notify(favorite, "favoriteAdded");
                        return favorite;
                    },
                    (error: Error) => {
                        this._notify(error, "favoriteError");
                        throw error;
                    }
                );
            },
            (error: Error) => {
                this._notify(error, "favoriteError");
                throw error;
            }
        );
    }

    public addTeamFavorite(teamId: string, favorite: Favorite): Promise<Favorite> {
        if (!this.canUseFavorites()) {
            this.logNoEditPermissionError();
            return Promise.resolve(favorite);
        }

        return this.getProvider(favorite.artifactType).then(
            provider => {
                const favoritesClient = this.pageContext.getRestClient<IFavoriteRestClient>("IFavoriteRestClient", {
                    serviceInstanceType: (provider as FavoriteProvider).serviceIdentifier
                });
                return favoritesClient.createFavoriteOfOwner(favorite, "Team", teamId).then(
                    favorite => {
                        this._notify(favorite, "favoriteAdded");
                        return favorite;
                    },
                    (error: Error) => {
                        this._notify(error, "favoriteError");
                        throw error;
                    }
                );
            },
            (error: Error) => {
                this._notify(error, "favoriteError");
                throw error;
            }
        );
    }

    /**
     * Remove (un-favorite) the given favorite item
     */
    public removeFavorite(favorite: Favorite): Promise<void> {
        if (!this.canUseFavorites()) {
            this.logNoEditPermissionError();
            return Promise.resolve();
        }

        return this.getProvider(favorite.artifactType).then(
            provider => {
                const favoritesClient = this.pageContext.getRestClient<IFavoriteRestClient>("IFavoriteRestClient", {
                    serviceInstanceType: (provider as FavoriteProvider).serviceIdentifier
                });
                return favoritesClient
                    .deleteFavoriteById(favorite.id, favorite.artifactType, favorite.artifactScope.type, favorite.artifactScope.id)
                    .then(
                        () => {
                            this._notify(favorite, "favoriteRemoved");
                        },
                        (error: Error) => {
                            this._notify(error, "favoriteError");
                            throw error;
                        }
                    );
            },
            (error: Error) => {
                this._notify(error, "favoriteError");
                throw error;
            }
        );
    }

    public removeTeamFavorite(teamId: string, favorite: Favorite): Promise<void> {
        if (!this.canUseFavorites()) {
            this.logNoEditPermissionError();
            return Promise.resolve();
        }

        return this.getProvider(favorite.artifactType).then(
            provider => {
                const favoritesClient = this.pageContext.getRestClient<IFavoriteRestClient>("IFavoriteRestClient", {
                    serviceInstanceType: (provider as FavoriteProvider).serviceIdentifier
                });
                return favoritesClient
                    .deleteFavoriteOfOwnerById(
                        favorite.id,
                        "Team",
                        teamId,
                        favorite.artifactType,
                        favorite.artifactScope.type,
                        favorite.artifactScope.id
                    )
                    .then(
                        () => {
                            this._notify(favorite, "favoriteRemoved");
                        },
                        (error: Error) => {
                            this._notify(error, "favoriteError");
                            throw error;
                        }
                    );
            },
            (error: Error) => {
                this._notify(error, "favoriteError");
                throw error;
            }
        );
    }

    public getProvider(artifactType: string): Promise<FavoriteProvider | null> {
        if (!this.canUseFavorites()) {
            this.logNoEditPermissionError();
            return Promise.resolve(null);
        }

        // Look in shared data first
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const sharedDataFavoriteProviders = contributionService.getSharedData<{ [artifactType: string]: FavoriteProvider }>("_favoriteProviders");
        if (sharedDataFavoriteProviders) {
            const provider = sharedDataFavoriteProviders[artifactType];
            if (provider) {
                return Promise.resolve(provider);
            }
        }

        return this.getProviders().then(providersByType => {
            return providersByType[artifactType];
        });
    }

    public getProviders(): Promise<{ [artifactType: string]: FavoriteProvider }> {
        if (!this.canUseFavorites()) {
            this.logNoEditPermissionError();
            return Promise.resolve({});
        }

        if (!this.providersPromise) {
            const favoritesClient = this.pageContext.getRestClient<IFavoriteRestClient>("IFavoriteRestClient");
            this.providersPromise = favoritesClient.getFavoriteProviders().then(providers => {
                return providers.reduce(
                    (map, provider) => {
                        map[provider.artifactType] = provider;
                        return map;
                    },
                    {} as { [artifactType: string]: FavoriteProvider }
                );
            });
        }

        return this.providersPromise;
    }

    private logNoEditPermissionError(): void {
        console.error(
            "Favorites client UI should not expose or allow write operations with Favorites, when the active user does not have neccessary permission."
        );
    }
}

Services.add("IFavoritesService", { serviceFactory: FavoritesService });
